import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauthenticated
  const [companies, setCompanies] = useState([]);
  const [ready, setReady] = useState(false); // true once bootstrap completes
  const [activeCompanyId, setActiveCompanyId] = useState(
    () => localStorage.getItem("kosh_company_id") || ""
  );

  const loadCompanies = useCallback(async () => {
    try {
      const r = await api.get("/auth/companies");
      setCompanies(r.data);
      if (r.data.length > 0) {
        const saved = localStorage.getItem("kosh_company_id");
        const has = r.data.some((c) => c.id === saved);
        const pick = has ? saved : r.data[0].id;
        localStorage.setItem("kosh_company_id", pick);
        setActiveCompanyId(pick);
      } else {
        localStorage.removeItem("kosh_company_id");
        setActiveCompanyId("");
      }
    } catch (e) {
      setCompanies([]);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("kosh_token");
    if (!token) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      await loadCompanies();
    } catch {
      localStorage.removeItem("kosh_token");
      setUser(null);
    } finally {
      setReady(true);
    }
  }, [loadCompanies]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("kosh_token", r.data.access_token);
    setUser(r.data.user);
    await loadCompanies();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("kosh_token");
    localStorage.removeItem("kosh_company_id");
    setUser(null);
    setCompanies([]);
    setActiveCompanyId("");
  };

  const switchCompany = (id) => {
    localStorage.setItem("kosh_company_id", id);
    setActiveCompanyId(id);
    // trigger full reload of data by dispatching custom event
    window.dispatchEvent(new CustomEvent("kosh:company-changed", { detail: id }));
  };

  const refreshCompanies = loadCompanies;

  return (
    <AuthContext.Provider
      value={{ user, companies, activeCompanyId, ready, login, logout, switchCompany, refreshCompanies }}
    >
      {children}
    </AuthContext.Provider>
  );
}
